import React from 'react';
import { Link } from 'react-router-dom';
import {
  AuditOutlined,
  CheckCircleOutlined,
  FileDoneOutlined,
  FileTextOutlined,
  GlobalOutlined,
  MailOutlined,
  SafetyCertificateOutlined,
  StopOutlined,
  TeamOutlined,
  UserOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useCommunityLinks } from '../../../core/config/useCommunityLinks';
import LegalPage, { LegalNavigationItem, LegalSummaryItem } from './LegalPage';
import LegalSection from './LegalSection';

const UPDATED_AT = 'August 12, 2026';

const navigation: readonly LegalNavigationItem[] = [
  { id: 'agreement', label: 'Agreement', icon: <FileDoneOutlined /> },
  { id: 'services', label: 'Our services', icon: <GlobalOutlined /> },
  { id: 'candidates', label: 'For candidates', icon: <UserOutlined /> },
  { id: 'recruiters', label: 'For recruiters', icon: <TeamOutlined /> },
  { id: 'acceptable-use', label: 'Acceptable use', icon: <StopOutlined /> },
  { id: 'content', label: 'Content & licenses', icon: <FileTextOutlined /> },
  { id: 'disclaimers', label: 'Disclaimers', icon: <WarningOutlined /> },
  { id: 'liability', label: 'Liability', icon: <SafetyCertificateOutlined /> },
  { id: 'law', label: 'Governing law', icon: <AuditOutlined /> },
  { id: 'contact', label: 'Contact', icon: <MailOutlined /> },
] as const;

const summaryItems: readonly LegalSummaryItem[] = [
  {
    title: 'Use the platform honestly',
    description:
      'Provide accurate information, respect other people, and follow applicable law.',
    icon: <SafetyCertificateOutlined />,
  },
  {
    title: 'Keep listings accurate',
    description:
      'Recruiters are responsible for lawful, current, and non-misleading opportunities.',
    icon: <FileTextOutlined />,
  },
  {
    title: 'Hiring decisions stay human',
    description:
      'We provide discovery and career tools; employers make hiring decisions.',
    icon: <UserOutlined />,
  },
] as const;

const TermsAndConditions: React.FC = () => {
  const { contactEmail } = useCommunityLinks();

  return (
    <LegalPage
      title="Terms and Conditions"
      description="Clear rules for using Jobs & Opportunities."
      updatedAt={UPDATED_AT}
      icon={<FileDoneOutlined />}
      navigation={navigation}
      summaryTitle="The short version"
      summaryItems={summaryItems}
    >
      <LegalSection id="agreement" number="01" title="Agreement to these terms">
        <p>
          These Terms and Conditions govern your access to and use of Jobs &amp;
          Opportunities, including job listings, applications, alerts, recruiter
          registration, resume tools, job-fit features, and related services. By
          accessing or using the service, you agree to these terms and our{' '}
          <Link to="/privacy">Privacy Policy</Link>.
        </p>
        <p>
          If you use the service for an organization, you represent that you are
          authorized to accept these terms on its behalf. If you do not agree,
          do not use the service.
        </p>
      </LegalSection>

      <LegalSection
        id="services"
        number="02"
        title="Using Jobs & Opportunities"
      >
        <p>
          We provide a platform that helps candidates discover opportunities,
          submit applications, receive alerts, and use career tools. We also
          help recruiters publish roles and review applications. Features may
          change, be unavailable in some locations, or depend on third-party
          services.
        </p>
        <p>
          You must be legally able to enter into these terms and meet the
          minimum age required to seek employment or recruit candidates in your
          location.
        </p>
      </LegalSection>

      <div className="legal-responsibilities">
        <LegalSection id="candidates" title="For candidates">
          <ul className="legal-check-list">
            <li>
              <CheckCircleOutlined /> Provide truthful, current application
              information.
            </li>
            <li>
              <CheckCircleOutlined /> Upload materials you have the right to
              share.
            </li>
            <li>
              <CheckCircleOutlined /> Apply only for legitimate employment
              purposes.
            </li>
            <li>
              <CheckCircleOutlined /> Evaluate employers and opportunities
              independently.
            </li>
          </ul>
        </LegalSection>

        <LegalSection id="recruiters" title="For recruiters">
          <ul className="legal-check-list">
            <li>
              <CheckCircleOutlined /> Publish accurate, lawful, and current job
              listings.
            </li>
            <li>
              <CheckCircleOutlined /> Have authority to recruit for each
              advertised role.
            </li>
            <li>
              <CheckCircleOutlined /> Handle candidate data lawfully and
              confidentially.
            </li>
            <li>
              <CheckCircleOutlined /> Make fair decisions without unlawful
              discrimination.
            </li>
          </ul>
        </LegalSection>
      </div>

      <LegalSection
        id="acceptable-use"
        title="Acceptable use"
        className="legal-callout legal-callout--warning"
      >
        <p>You must not:</p>
        <ul>
          <li>
            Post false, deceptive, discriminatory, or fraudulent opportunities.
          </li>
          <li>Harass, exploit, impersonate, or harm another person.</li>
          <li>
            Scrape, copy, or collect data through unauthorized automated means.
          </li>
          <li>
            Bypass security, probe vulnerabilities, or disrupt the service.
          </li>
          <li>
            Upload malware or content that violates law or third-party rights.
          </li>
          <li>Use candidate information for unrelated marketing or resale.</li>
        </ul>
      </LegalSection>

      <LegalSection id="content" title="Content and licenses">
        <p>
          You keep ownership of content you submit. You grant us a limited,
          worldwide, non-exclusive license to host, reproduce, process, and
          display that content only as needed to operate, secure, and improve
          the service. For example, we may display a recruiter&apos;s job
          listing or transmit a candidate&apos;s application to the relevant
          hiring team.
        </p>
        <p>
          You represent that you have the rights and permissions needed for your
          content. The service, brand, software, and original platform content
          remain owned by us or our licensors and may not be copied or exploited
          except as permitted by law or written authorization.
        </p>
      </LegalSection>

      <LegalSection id="communications" title="Communications">
        <p>
          When you request job alerts, application updates, or recruiter access,
          you authorize us to send the related operational communications.
          Marketing or optional messaging choices may be withdrawn using the
          available unsubscribe method or by contacting us.
        </p>
      </LegalSection>

      <LegalSection
        id="third-parties"
        title="Third-party services and employers"
      >
        <p>
          Job listings, employer websites, communication channels, and other
          third-party services are controlled by their respective operators.
          Their terms and privacy practices apply to your interactions with
          them. We do not control their availability, conduct, or hiring
          process.
        </p>
      </LegalSection>

      <LegalSection
        id="disclaimers"
        number="03"
        title="No employment guarantee"
      >
        <p>
          Jobs &amp; Opportunities does not guarantee that listings are
          suitable, that candidates will receive responses, interviews, or job
          offers, or that recruiters will fill a role. Job-fit scores, resume
          suggestions, and extracted information are automated aids and may
          contain errors. Verify important information before relying on it.
        </p>
        <p>
          To the extent permitted by law, the service is provided “as is” and
          “as available,” without warranties of uninterrupted availability,
          accuracy, fitness for a particular purpose, or non-infringement.
        </p>
      </LegalSection>

      <LegalSection id="liability" title="Limitation of liability">
        <p>
          To the fullest extent permitted by applicable law, Jobs &amp;
          Opportunities and its operators will not be liable for indirect,
          incidental, special, consequential, or punitive damages, loss of data,
          lost profits, or employment-related decisions arising from use of the
          service. Nothing in these terms excludes liability that cannot legally
          be excluded or limited.
        </p>
      </LegalSection>

      <LegalSection id="suspension" title="Suspension and termination">
        <p>
          You may stop using the service at any time. We may restrict or end
          access, remove content, or suspend features when reasonably necessary
          to protect users, enforce these terms, comply with law, or maintain
          service security. Provisions that by their nature should survive will
          remain effective after termination.
        </p>
      </LegalSection>

      <LegalSection id="law" title="Governing law and disputes">
        <p>
          These terms are governed by the laws applicable to the organization
          operating Jobs &amp; Opportunities, without overriding any mandatory
          consumer or employment protections that apply to you. Disputes will be
          submitted to the competent courts unless applicable law requires a
          different forum.
        </p>
      </LegalSection>

      <LegalSection id="changes" title="Changes to these terms">
        <p>
          We may update these terms as the service or legal requirements change.
          The updated date above shows when the latest version became effective.
          We will provide additional notice for material changes when required.
          Continued use after the effective date constitutes acceptance where
          permitted by law.
        </p>
      </LegalSection>

      <LegalSection id="contact" title="Contact us">
        <p>
          Questions about these terms may be sent
          {contactEmail ? (
            <>
              {' '}
              to <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.
            </>
          ) : (
            <> through the contact method published in the website footer.</>
          )}
        </p>
      </LegalSection>
    </LegalPage>
  );
};

export default TermsAndConditions;
